
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
    baseUrl,
    ipAddress,
    userLocation,
    userName
} from '../Univ.js';

const SalesReturn = () => {
    const { sale_pharm } = useParams();

    const [opbillData, setOpbillData] = useState([]);
    const [viewPharmData, setViewPharmData] = useState([]);

    // Original quantities from the completed sale. Never modify these.
    const [originalQty, setOriginalQty] = useState([]);

    // Editable return quantities and their calculated amounts.
    const [lres, setLres] = useState([]);
    const [lnet, setLnet] = useState([]);

    const [subtotal, setSubTotal] = useState(0);
    const [disc, setDisc] = useState(0);
    const [disPer, setDisPer] = useState(0);
    const [total, setTotal] = useState(0);

    const [discountMode, setDiscountMode] = useState('amount');
    const [submitting, setSubmitting] = useState(false);

    // Load the original bill and preserve the sold quantities.
    useEffect(() => {
        let cancelled = false;

        const fetchBill = async () => {
            try {
                const response = await axios.get(
                    `${baseUrl}pharm-invoice-other/${sale_pharm}`
                );

                if (cancelled) return;

                const data = response.data || [];
                setOpbillData(data);

                const quantities = data[0]?.qty
                    ? data[0].qty.split(',').map(q => q.trim())
                    : [];

                const amounts = data[0]?.netamount
                    ? data[0].netamount.split(',').map(
                        amount => parseFloat(amount) || 0
                    )
                    : [];

                setOriginalQty(quantities);
                setLres([...quantities]);
                setLnet(amounts);
                setDisc(parseFloat(data[0]?.disc) || 0);
            } catch (error) {
                console.error('Error loading pharmacy bill:', error);

                if (!cancelled) {
                    Swal.fire(
                        'Error',
                        'Unable to load the pharmacy bill.',
                        'error'
                    );
                }
            }
        };

        fetchBill();

        return () => {
            cancelled = true;
        };
    }, [sale_pharm]);

    // Fetch the pharmacy stock/product information for each bill item.
    useEffect(() => {
        let cancelled = false;

        const fetchProducts = async () => {
            const productIds = [
                ...new Set(
                    opbillData.flatMap(row =>
                        (row.product || '')
                            .split(',')
                            .map(id => id.trim())
                            .filter(Boolean)
                    )
                )
            ];

            if (productIds.length === 0) {
                setViewPharmData([]);
                return;
            }

            try {
                const results = await Promise.all(
                    productIds.map(async id => {
                        const response = await axios.get(
                            `${baseUrl}update-indent-use-bill/${id}/`
                        );
                        return response.data;
                    })
                );

                if (!cancelled) {
                    setViewPharmData(results);
                }
            } catch (error) {
                console.error('Error loading pharmacy products:', error);
            }
        };

        fetchProducts();

        return () => {
            cancelled = true;
        };
    }, [opbillData]);

    const arraySum = values =>
        values.reduce(
            (sum, value) => sum + (parseFloat(value) || 0),
            0
        );

    // Change return quantity without changing the original sold quantity.
    const handleAmountChange = (e, subIndex, unitAmount) => {
        const inputValue = e.target.value;
        const maxQty = parseFloat(originalQty[subIndex]) || 0;
        const price = parseFloat(unitAmount) || 0;

        // Permit clearing the field while the user is editing.
        if (inputValue === '') {
            setLres(previous =>
                previous.map((value, index) =>
                    index === subIndex ? '' : value
                )
            );

            setLnet(previous =>
                previous.map((value, index) =>
                    index === subIndex ? 0 : value
                )
            );

            return;
        }

        let qty = parseFloat(inputValue);

        if (!Number.isFinite(qty) || qty < 0) {
            qty = 0;
        }

        // Cap at the original sold quantity, without an alert.
        if (qty > maxQty) {
            qty = maxQty;
        }

        // Keep the original input string when valid, so values like
        // "1." remain editable while the user types a decimal.
        const updatedValue =
            parseFloat(inputValue) > maxQty
                ? String(maxQty)
                : inputValue;

        setLres(previous =>
            previous.map((value, index) =>
                index === subIndex ? updatedValue : value
            )
        );

        setLnet(previous =>
            previous.map((value, index) =>
                index === subIndex
                    ? Number((qty * price).toFixed(2))
                    : value
            )
        );
    };

    // Update the discount percentage.
    const handleDisPerChange = e => {
        const percentage = Math.min(
            100,
            Math.max(0, parseFloat(e.target.value) || 0)
        );

        setDiscountMode('percentage');
        setDisPer(percentage);
    };

    // Update the discount amount.
    const handleDiscChange = e => {
        const amount = Math.max(0, parseFloat(e.target.value) || 0);

        setDiscountMode('amount');
        setDisc(amount);
    };

    // Recalculate subtotal and total whenever quantities or discount change.
    useEffect(() => {
        const newSubtotal = arraySum(lnet);

        const discountAmount =
            discountMode === 'percentage'
                ? newSubtotal * (disPer / 100)
                : Math.min(newSubtotal, Math.max(0, disc));

        setSubTotal(newSubtotal);
        setDisc(Number(discountAmount.toFixed(2)));
        setTotal(
            Number(Math.max(0, newSubtotal - discountAmount).toFixed(2))
        );
    }, [lnet, disc, disPer, discountMode]);


const handleKeyDown = (e, flatIndex) => {
    if (e.key.toLowerCase() === 'z') {
        e.preventDefault();
        handleDeleteRow(flatIndex);
    }
};

const handleDeleteRow = (flatIndex) => {
    if (!Number.isInteger(flatIndex) || flatIndex < 0) return;

    const confirmed = window.confirm(
        'Are you sure you want to remove this drug from the return?'
    );

    if (!confirmed) return;

    // Remove the item at the same index from all parallel arrays.
    setLres(prev => prev.filter((_, i) => i !== flatIndex));
    setOriginalQty(prev => prev.filter((_, i) => i !== flatIndex));
    setLnet(prev => prev.filter((_, i) => i !== flatIndex));

    // Find the correct row and local item index in the bill.
    setOpbillData(prev => {
        let offset = 0;

        return prev.map(row => {
            const products = String(row.product || '')
                .split(',')
                .filter(Boolean);

            const quantities = String(row.qty || '').split(',');
            const amounts = String(row.netamount || '').split(',');

            const localIndex = flatIndex - offset;
            offset += products.length;

            if (
                localIndex < 0 ||
                localIndex >= products.length
            ) {
                return row;
            }

            products.splice(localIndex, 1);
            quantities.splice(localIndex, 1);

            if (localIndex < amounts.length) {
                amounts.splice(localIndex, 1);
            }

            return {
                ...row,
                product: products.join(','),
                qty: quantities.join(','),
                netamount: amounts.join(',')
            };
        });
    });
};

    const formSubmit = async e => {
        e.preventDefault();

        if (submitting) return;

        const bill = opbillData[0];

        if (!bill) {
            Swal.fire('Error', 'No bill data found.', 'error');
            return;
        }

        const ids = opbillData.flatMap(row =>
            (row.product || '')
                .split(',')
                .map(id => Number(id.trim()))
                .filter(Number.isFinite)
        );

        if (ids.length === 0) {
            Swal.fire('Error', 'There are no products to return.', 'error');
            return;
        }

        // Ensure all submitted quantities are valid and within sold quantities.
        const qtys = ids.map((_, index) => {
            const enteredQty = parseFloat(lres[index]) || 0;
            const soldQty = parseFloat(originalQty[index]) || 0;

            return Math.max(0, Math.min(enteredQty, soldQty));
        });

        const amounts = ids.map((_, index) =>
            Number((qtys[index] * (
                parseFloat(
                    viewPharmData.find(
                        product => product.id === ids[index]
                    )?.product?.uamount
                ) || 0
            )).toFixed(2))
        );

        if (qtys.every(qty => qty === 0)) {
            Swal.fire(
                'Check quantity',
                'Enter a return quantity greater than zero.',
                'warning'
            );
            return;
        }

        setSubmitting(true);

        try {
            const returnResponse = await axios.post(
                `${baseUrl}get_sale_return/`
            );

            const saleReturn = returnResponse.data.value;
            const formData = new FormData();

            formData.append('return_no', saleReturn);
            formData.append(
                'return_type',
                document.getElementById('dbType').value
            );
            formData.append('sale_id', bill.id);
            formData.append('pat', bill.pat?.id ?? '');
            formData.append('patient', bill.patient ?? '');
            formData.append('product', ids.join(','));
            formData.append('qty', qtys.join(','));
            formData.append('netamount', amounts.join(','));
            formData.append('total', total.toFixed(2));
            formData.append(
                'details',
                document.getElementById('details').value
            );
            formData.append('status', '1');
            formData.append('addedby', userName);
            formData.append('ip_add', ipAddress);
            formData.append('location', userLocation);

            await axios.post(
                `${baseUrl}pharm-sale-return/`,
                formData
            );

            // Restore the returned quantities to stock.
            // Each product is updated using its own current stock quantity.
            await Promise.all(
    ids.map(async (id, index) => {
        const indentUse = viewPharmData.find(
            item => Number(item.id) === Number(id)
        );

        if (!indentUse || Number(qtys[index]) <= 0) return;

        const currentStock = parseFloat(indentUse.qty) || 0;

        await axios.put(
            `${baseUrl}update-indent-use/${id}/`,
            {
                product: Number(indentUse.product?.id),
                qty: currentStock + Number(qtys[index]),
                ip_add: ipAddress,
            }
        );
    })
);

            await Swal.fire({
                title: 'Pharmacy Return Bill Generated.',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });

            window.location.href =
                `/pharm-invoice-return/${saleReturn}/`;
        } catch (error) {
            console.error('Error submitting pharmacy return:', error);

            Swal.fire(
                'Error!',
                'Failed to save the return. Please check the bill and try again.',
                'error'
            );
        } finally {
            setSubmitting(false);
        }
    };

    const getProduct = id =>
        viewPharmData.find(product => product.id === Number(id));

    return (
        <section className="container mt-4">
            <form onSubmit={formSubmit}>
                <div className="table-responsive mb-3">
                    <table className="erp-table-modern">
                        <thead>
                            <tr style={{
                                backgroundColor: '#1bc5ce',
                                color: 'white'
                            }}>
                                <th>Patient ID</th>
                                <th>Patient Name</th>
                                <th>Sale ID</th>
                                <th>Billed On</th>
                            </tr>
                        </thead>

                        <tbody>
                            {opbillData.map((bill, index) => (
                                <tr key={bill.id ?? index}>
                                    <td>{bill.pat?.id ?? ''}</td>
                                    <td>{bill.patient ?? ''}</td>
                                    <td>{bill.sale_id ?? ''}</td>
                                    <td>{bill.add_date ?? ''}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="table-responsive">
                    <table className="erp-table-modern">
                        <thead>
                            <tr style={{
                                backgroundColor: '#1bc5ce',
                                color: 'white'
                            }}>
                                <th>ID</th>
                                <th>Drug Name</th>
                                <th>Return Qty</th>
                                <th>Batch</th>
                                <th>Expiry</th>
                                <th>Qty in Stock</th>
                                <th>MRP 1s</th>
                                <th>Net Amount</th>
                                <th>Action</th>
                            </tr>
                        </thead>

                        <tbody>
                            {opbillData.map((row, rowIndex) => {
                                const products = (row.product || '')
                                    .split(',')
                                    .filter(Boolean);

                                const offset = opbillData
                                    .slice(0, rowIndex)
                                    .reduce(
                                        (count, previousRow) =>
                                            count +
                                            (previousRow.product || '')
                                                .split(',')
                                                .filter(Boolean).length,
                                        0
                                    );

                                return products.map((id, subIndex) => {
                                    const flatIndex = offset + subIndex;
                                    const product = getProduct(id);
                                    const unitAmount =
                                        parseFloat(product?.product?.uamount) || 0;

                                    return (
                                        <tr
                                            key={`${rowIndex}-${subIndex}`}
                                        >
                                            <td>{product?.id ?? id}</td>

                                            <td>
                                                {product?.product?.product ?? ''}
                                            </td>

                                            <td>
                                                <input
                                                    type="number"
                                                    style={{ width: 90 }}
                                                    className="form-control"
                                                    name={`qty-${flatIndex}`}
                                                    value={lres[flatIndex] ?? ''}
                                                    min={0}
                                                    max={
                                                        Number(originalQty[flatIndex]) || 0
                                                    }
                                                    step="any"
                                                    onChange={e =>
                                                        handleAmountChange(
                                                            e,
                                                            flatIndex,
                                                            unitAmount
                                                        )
                                                    }

onKeyDown={e => handleKeyDown(e, flatIndex)}
                                                    required
                                                />

                                                <small className="text-muted">
                                                    Sold: {originalQty[flatIndex] ?? 0}
                                                </small>
                                            </td>

                                            <td>
                                                {product?.product?.batch ?? ''}
                                            </td>

                                            <td>
                                                {product?.product?.expiry ?? ''}
                                            </td>

                                            <td>{product?.qty ?? 0}</td>

                                            <td>
                                                {unitAmount.toFixed(2)}
                                            </td>

                                            <td>
                                                {(parseFloat(lnet[flatIndex]) || 0)
                                                    .toFixed(2)}
                                            </td>

<td>
    <button
        type="button"
        className="btn btn-sm btn-danger"
        onClick={() => handleDeleteRow(flatIndex)}
        title="Remove this drug from the return"
    >
        Delete
    </button>
</td>

                                        </tr>
                                    );
                                });
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="row mt-4">
                    <div className="col-md-6">
                        <div className="form-group mb-3">
                            <label className="fw-bold">Subtotal (₹)</label>
                            <input
                                className="form-control"
                                type="number"
                                value={subtotal.toFixed(2)}
                                readOnly
                            />
                        </div>

                        <div className="form-group mb-3">
                            <label className="fw-bold">
                                Discount Percentage (%)
                            </label>
                            <input
                                className="form-control"
                                type="number"
                                min={0}
                                max={100}
                                step="any"
                                value={disPer}
                                onChange={handleDisPerChange}
                            />
                        </div>

                        <div className="form-group mb-3">
                            <label className="fw-bold">
                                Discount Amount (₹)
                            </label>
                            <input
                                className="form-control"
                                type="number"
                                min={0}
                                step="any"
                                value={disc}
                                onChange={handleDiscChange}
                            />
                        </div>

                        <div className="form-group mb-3">
                            <label className="fw-bold">Total (₹)</label>
                            <input
                                className="form-control"
                                type="number"
                                value={total.toFixed(2)}
                                readOnly
                            />
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="form-group mb-3">
                            <label>Choose Payment Type</label>
                            <select
                                className="form-select"
                                name="dbType"
                                id="dbType"
                            >
                                <option value="Cash">Cash</option>
                                <option value="Cashless">Cashless</option>
                            </select>
                        </div>

                        <div className="form-group mb-3">
                            <label>Details</label>
                            <textarea
                                className="form-control"
                                name="details"
                                id="details"
                                rows={3}
                            />
                        </div>
                    </div>
                </div>

                <div className="d-flex justify-content-center mb-5">
                    <button
                        type="submit"
                        className="btn btn-primary mt-4"
                        disabled={submitting}
                    >
                        {submitting ? 'Submitting...' : 'Submit Return'}
                    </button>
                </div>
            </form>
        </section>
    );
};

export default SalesReturn;
